import { useMemo, useState, useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import { RoofMapPanelProps, RoofData } from "../types";
import { RoofSelector } from "./RoofSelector";
import { RoofModel } from "./RoofModel";
import {
  CAMERA_SETTINGS,
  ORBIT_CONTROLS_SETTINGS,
  LIGHTING_SETTINGS,
} from "../constants";
import { ShadowMapSettings } from "../../../../../shared/theme";
import { RoofTreeRow } from "../../../models";
import { useRoofContextMenu } from "../../../hooks/useRoofContextMenu";

export const RoofMapPanel = ({
  treeData,
  selectedRow,
  onSelectRow,
  onSetOnline,
  onSetOffline,
  onInstall,
  onUninstall,
  onRepair,
  onNewSparePart,
  onEditPart,
  onSetInitialMetric,
  isLoading = false,
}: RoofMapPanelProps) => {
  const theme = useTheme();

  const { getMenuOptions } = useRoofContextMenu({
    onInstall,
    onUninstall,
    onRepair,
    onNewSparePart,
    onEditPart,
    onSetInitialMetric,
    onCorrectDates: () => {}, // Not applicable in map view
  });

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  // Use a ref to keep getMenuOptions up-to-date without triggering re-registration
  const getMenuOptionsRef = useRef(getMenuOptions);
  getMenuOptionsRef.current = getMenuOptions;

  // Register context menu config only once on mount
  useEffect(() => {
    registerConfig({
      id: "roofMapContext",
      getOptions: (data: unknown) =>
        getMenuOptionsRef.current(data as RoofTreeRow),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for selected roof in the 3D view
  // Initialize from selectedRow if it's an equipment or location (not a container)
  const [selectedRoofId, setSelectedRoofId] = useState<number | null>(() => {
    if (
      selectedRow &&
      (selectedRow.rowType === "equipment" ||
        selectedRow.rowType === "location")
    ) {
      return selectedRow.EquipmentId;
    }
    return null;
  });

  // State for hovered panel (just store the location data, no 3D position needed)
  const [hoveredData, setHoveredData] = useState<RoofTreeRow | null>(null);
  const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(
    null,
  );

  // Transform tree data into RoofData array
  const roofs: RoofData[] = useMemo(() => {
    const roofMap = new Map<number, RoofData>();

    for (const row of treeData) {
      if (row.rowType === "equipment") {
        // This is a roof row
        roofMap.set(row.EquipmentId, {
          roofId: row.EquipmentId,
          roofName: row.EquipmentName ?? `Roof #${row.EquipmentId}`,
          isOnline: row.Status === "Online",
          heats: row.Heats ?? 0,
          totalHeats: row.TotalHeats ?? 0,
          locations: [],
        });
      } else {
        // This is a location row
        const roof = roofMap.get(row.EquipmentId);
        if (roof) {
          roof.locations.push(row);
        }
      }
    }

    return Array.from(roofMap.values());
  }, [treeData]);

  // Auto-select first roof if none selected
  useEffect(() => {
    if (selectedRoofId === null && roofs.length > 0) {
      // Prefer online roof, otherwise first roof
      const onlineRoof = roofs.find((r) => r.isOnline);
      setSelectedRoofId(onlineRoof?.roofId ?? roofs[0].roofId);
    }
  }, [roofs, selectedRoofId]);

  // Get the currently selected roof data
  const selectedRoof = useMemo(() => {
    return roofs.find((r) => r.roofId === selectedRoofId) ?? null;
  }, [roofs, selectedRoofId]);

  // Get selected location ID from selectedRow
  const selectedLocationId = useMemo(() => {
    if (!selectedRow || selectedRow.rowType === "equipment") return null;
    return selectedRow.id;
  }, [selectedRow]);

  // Handle roof selection from RoofSelector
  const handleSelectRoof = (roofId: number) => {
    setSelectedRoofId(roofId);
    // Find the equipment row for this roof and update selectedRow
    const roofRow = treeData.find(
      (row) => row.rowType === "equipment" && row.EquipmentId === roofId,
    );
    if (roofRow) {
      onSelectRow(roofRow);
    }
  };

  // Handle panel selection
  const handleSelectLocation = (row: RoofTreeRow | null) => {
    onSelectRow(row);
  };

  // Handle panel hover
  const handleHoverLocation = (locationId: string | null) => {
    setHoveredLocationId(locationId);

    if (locationId === null) {
      setHoveredData(null);
      return;
    }

    // Find the location data
    const locationData = selectedRoof?.locations.find(
      (loc) =>
        `location-${selectedRoof.roofId}-${loc.PositionId}` === locationId,
    );

    setHoveredData(locationData ?? null);
  };

  // Handle context menu on canvas
  const handleCanvasContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    // If hovering over a panel, show context menu
    if (hoveredData && hoveredData.rowType !== "equipment") {
      onSelectRow(hoveredData);
      showContextMenu(event, hoveredData, "roofMapContext");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
    >
      {/* Roof selector bar at top */}
      <RoofSelector
        roofs={roofs}
        selectedRoofId={selectedRoofId}
        onSelectRoof={handleSelectRoof}
        onSetOnline={onSetOnline}
        onSetOffline={onSetOffline}
      />

      {/* 3D Canvas */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          minHeight: 400,
        }}
        onContextMenu={handleCanvasContextMenu}
      >
        {isLoading ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (theme) =>
                theme.palette.eqp.viewer.loadingOverlay,
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        ) : null}

        <Canvas
          style={{
            // Canvas gradient - Light blue gradient
            background: `linear-gradient(180deg, ${theme.palette.eqp.viewer.canvasGradient.start} 0%, ${theme.palette.eqp.viewer.canvasGradient.middle} 50%, ${theme.palette.eqp.viewer.canvasGradient.end} 100%)`,
          }}
          camera={{
            position: CAMERA_SETTINGS.position,
            fov: CAMERA_SETTINGS.fov,
            near: CAMERA_SETTINGS.near,
            far: CAMERA_SETTINGS.far,
          }}
          shadows
        >
          {/* Lighting */}
          <ambientLight intensity={LIGHTING_SETTINGS.ambientIntensity} />
          <directionalLight
            position={LIGHTING_SETTINGS.directionalPosition}
            intensity={LIGHTING_SETTINGS.directionalIntensity}
            castShadow
            shadow-mapSize-width={ShadowMapSettings.SIZE}
            shadow-mapSize-height={ShadowMapSettings.SIZE}
          />

          {/* Camera controls */}
          <OrbitControls
            enablePan={ORBIT_CONTROLS_SETTINGS.enablePan}
            enableZoom={ORBIT_CONTROLS_SETTINGS.enableZoom}
            enableRotate={ORBIT_CONTROLS_SETTINGS.enableRotate}
            minDistance={ORBIT_CONTROLS_SETTINGS.minDistance}
            maxDistance={ORBIT_CONTROLS_SETTINGS.maxDistance}
            minPolarAngle={ORBIT_CONTROLS_SETTINGS.minPolarAngle}
            maxPolarAngle={ORBIT_CONTROLS_SETTINGS.maxPolarAngle}
            enableDamping={ORBIT_CONTROLS_SETTINGS.enableDamping}
            dampingFactor={ORBIT_CONTROLS_SETTINGS.dampingFactor}
          />

          {/* Roof model */}
          {selectedRoof ? (
            <RoofModel
              roofData={selectedRoof}
              selectedLocationId={selectedLocationId}
              hoveredLocationId={hoveredLocationId}
              onSelectLocation={handleSelectLocation}
              onHoverLocation={handleHoverLocation}
            />
          ) : null}
        </Canvas>

        {/* Screen-space tooltip for hovered panel */}
        {hoveredData ? (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              zIndex: 20,
              pointerEvents: "none",
            }}
          >
            <Paper
              elevation={4}
              sx={{
                padding: (theme) => theme.spacing(1, 1.5),
                backgroundColor: (theme) =>
                  theme.palette.eqp.viewer.tooltipBackground,
                color: (theme) => theme.palette.common.white,
                minWidth: 180,
                maxWidth: 250,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  marginBottom: (theme) => theme.spacing(0.5),
                }}
              >
                {hoveredData.PositionName ?? "Unknown Position"}
              </Typography>

              {hoveredData.HasAssignedPart ? (
                <>
                  {hoveredData.PartInfo?.SerialNumber ||
                  hoveredData.L2PartName ? (
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: (theme) => theme.palette.grey[400],
                        marginBottom: (theme) => theme.spacing(0.5),
                      }}
                    >
                      {hoveredData.PartInfo?.SerialNumber ??
                        hoveredData.L2PartName}
                    </Typography>
                  ) : null}
                  <Divider
                    sx={{
                      backgroundColor: (theme) => theme.palette.grey[700],
                      margin: (theme) => theme.spacing(0.5, 0),
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      marginTop: (theme) => theme.spacing(0.25),
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ color: (theme) => theme.palette.grey[400] }}
                    >
                      Heats Since Install:
                    </Box>
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {hoveredData.Heats?.toLocaleString() ?? "-"}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      marginTop: (theme) => theme.spacing(0.25),
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ color: (theme) => theme.palette.grey[400] }}
                    >
                      Heats Since Repair:
                    </Box>
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {hoveredData.HeatsSinceRepair?.toLocaleString() ?? "-"}
                    </Box>
                  </Box>
                  {hoveredData.TotalHeats !== undefined ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.8rem",
                        marginTop: (theme) => theme.spacing(0.25),
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ color: (theme) => theme.palette.grey[400] }}
                      >
                        Total Heats:
                      </Box>
                      <Box component="span" sx={{ fontWeight: 500 }}>
                        {hoveredData.TotalHeats.toLocaleString()}
                      </Box>
                    </Box>
                  ) : null}
                </>
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontStyle: "italic",
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  No part installed
                </Typography>
              )}
            </Paper>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
